import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MainLayout } from '../components/MainLayout';
import { FormSectionTabs } from '../components/FormSectionTabs';
import { SOFPTable } from '../components/SOFPTable';
import { SOCIETable } from '../components/SOCIETable';
import { SORETable } from '../components/SORETable';
import { FormField } from '../components/FormField';
import { AuditorsInformationTable } from '../components/AuditorsInformationTable';
import { BusinessTable } from '../components/BusinessTable';
import { CLBGTable } from '../components/CLBGTable';
import { NoteShareRelatedPartyTable } from '../components/NoteShareRelatedPartyTable';
import { SidebarToggle } from '../components/SidebarToggle';
import { SOFPTableSG } from '../components/singapore/SOFPTableSG';
import { PPETableSG } from '../components/singapore/PPETableSG';

import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../components/Breadcrumb';
import { Separator } from '../components/Separator';
import PDFViewer from '../components/PDFViewer';
import { useHyperFormula } from '../hooks/useHyperFormula';
import { useValidation, toggleValidation, isValidationDisabled } from '../hooks/useValidation';
import { useValidationSG } from '../hooks/singapore/useValidationSG';
import { useCrossFieldNavigation } from '../hooks/useCrossFieldNavigation';
import { useDirectorCrossSectionSuggest } from '../hooks/useDirectorCrossSectionSuggest';
import { DirectorCrossSuggestBanner } from './DirectorCrossSuggestBanner';
import { useScopeFilter } from '../hooks/useScopeFilter';
import { FootnoteModal } from '../components/FootnoteModal';
import { TextBlockModal } from '../components/TextBlockModal';
import { YearEndChangeModal } from '../components/YearEndChangeModal';
import { ExpensesMethodChangeModal } from '../components/ExpensesMethodChangeModal';
import { EquityMethodChangeModal } from '../components/EquityMethodChangeModal';
import { SofpMethodChangeModal } from '../components/SofpMethodChangeModal';
import { CashFlowsMethodChangeModal } from '../components/CashFlowsMethodChangeModal';
import { CrossFieldNavigationMenu } from '../components/CrossFieldNavigationMenu';
import { SaveSuccessModal } from '../components/SaveSuccessModal';
import '../App.css';
import '../styles/sofp-table.css';
import '../styles/main-layout.css';
import '../styles/form-fields.css';
import '../styles/footnote-modal.css';

// Import your actual form structure
import formStructureCustom from '../form_structure_custom.json';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase_client';
import { loadMSICCodes } from '../services/msicService';
import { getTaxonomyFromS3 } from '../services/s3Service';
import { findNegativeWeightTargets } from '../utils/findNegativeTargets';
import { isMandatoryField, getFieldCode } from '../utils/mandatoryFields';
import { initializeXBRLMandatoryFields } from '../utils/xbrlMandatoryFields';
import { applyReadOnlyFields } from '../utils/readOnlyFields';
import { sectionConfig } from '../utils/sectionConfig';
import { setFootnoteModalContext, openFootnoteModal, closeFootnoteModal } from '../utils/footnoteModalBus';
import { initFootnoteAnchors, refreshFootnoteAnchors } from '../utils/footnoteAnchorManager';
import { setTextBlockModalContext, openTextBlockModal, closeTextBlockModal } from '../utils/textBlockModalBus';
import { setYearEndModalContext, openYearEndModal } from '../utils/yearEndModalBus';
import { setExpensesMethodChangeModalContext, openExpensesMethodChangeModal } from '../utils/ExpensesMethodChangeModalBus';
import { setEquityMethodChangeModalContext, openEquityMethodChangeModal } from '../utils/equityMethodChangeModalBus';
import { setSofpMethodChangeModalContext, openSofpMethodChangeModal } from '../utils/sofpMethodChangeModalBus';
import { setCashFlowsMethodChangeModalContext, openCashFlowsMethodChangeModal } from '../utils/cashFlowsMethodChangeModalBus';
import { PDF_VIEWER_CHANGED_EVENT, togglePdfViewerOpen, setPdfViewerOpen } from '../utils/pdfViewerBus';
import { getSidebarCollapsed, setSidebarCollapsed } from '../utils/sidebarBus';
import { SAVE_STATE_CHANGED_EVENT, startSaving, endSaving } from '../utils/saveStateBus';
import { setSaveSuccessModalContext, openSaveSuccessModal } from '../utils/saveSuccessModalBus';
import { setReloginModalContext, openReloginModal, setReloginGuard } from '../utils/reloginModalBus';
import { ensureFreshSession } from '../utils/sessionRecovery';
import { devLog } from '../utils/devLogger';
import {
  getReportNoteSortKeyFromHtml,
  compareReportNoteSortKeys,
  getTextBlockPreview,
} from '../utils/reportNoteOrder';

const NOTE_OTHER_SECTION_CODE = '730000_DisclosureOfNotesAndOtherExplanatoryInformationAbstract';

/** Warning only — not a validation error. Toggles via DOM so text-block edits do not re-render. */
function updateNoteOtherEmptyWarning() {
  const banner = document.getElementById('note-other-empty-warning') as HTMLElement | null;
  if (!banner) return;

  const sectionEl = document.getElementById(NOTE_OTHER_SECTION_CODE);
  if (!sectionEl) {
    banner.style.display = 'none';
    return;
  }

  const textareas = sectionEl.querySelectorAll<HTMLTextAreaElement>(
    '.text-block-wrapper textarea'
  );
  if (textareas.length === 0) {
    banner.style.display = 'none';
    return;
  }

  const allEmpty = Array.from(textareas).every(
    (ta) => !getTextBlockPreview(ta.value || '')
  );
  banner.style.display = allEmpty ? 'flex' : 'none';
}

/**
 * App - Main application component that mirrors your 1.0 UI structure exactly
 * Uses MainLayout (main.html) + FormSectionTabs + FormContent (form-tailwind.html)
 */
export function EditFilings() {
  // SINGLE SOURCE OF TRUTH: useRef stores all field values
  // Key format: "qname_realContextRef" (e.g., "ssmt:Land_asof_20250630_SeparateMember" or "ssmt-dei:NewCompanyRegistrationNumber_fromto_20240701_20250630")
  // Matches database format: { "realContext": { "qname": { "value": "..." } } }
  // DOM is just the UI layer that mirrors this ref
  const formDataRef = useRef<Record<string, string>>({});
  
  // Use refs for UI state (no re-renders) - manipulate DOM directly like 1.0
  const activeSectionRef = useRef<string>('');
  const activeEditorRef = useRef<string | null>(null);
  const isPdfViewerOpenRef = useRef(false); // keep as ref for other non-render logic
  const mainContentWrapRef = useRef<HTMLDivElement>(null);
  const leftTabsRef = useRef<HTMLDivElement>(null);
  const subContentRef = useRef<HTMLDivElement>(null);
  const stickyDropdownRef = useRef<HTMLDivElement>(null);
  const pdfColumnRef = useRef<HTMLDivElement>(null);
  const forcedSidebarCollapseRef = useRef(false);
  
  // Text block modal - refs only (NO STATE)
  const currentTextBlockRef = useRef<{ fieldName: string; fieldLabel: string; contextRef: string } | null>(null);

  /**
   * Note sequence toggle — DOM-only (NO useState).
   * Only reorders current-year text blocks in Note Policy / Note Other
   * (720000/730000) for editing. Does NOT change how metadata.sequence is saved.
   * Save uses unique DOM order (legacy); if toggle is ON, taxonomy DOM is
   * restored briefly before collect so sequences stay taxonomy-stable + unique.
   */
  const sortByNoteNoRef = useRef(false);
  const noteSeqBtnRef = useRef<HTMLButtonElement>(null);
  const noteSeqStateRef = useRef<HTMLSpanElement>(null);
  /**
   * Comment markers left in the original DOM spot before a flat reorder,
   * so OFF can insertBefore(marker) and restore exact taxonomy position
   * (e.g. Significant Accounting Policies before .nested-children).
   */
  const noteSeqOrigPlacementRef = useRef<Map<string, { marker: Comment }>>(new Map());
  
  // Keep minimal state only for initial load
  const [isFormReady, setIsFormReady] = useState(false); // Loading state
  const [isLoadingData, setIsLoadingData] = useState(false); // Track data loading state
  const [breadcrumbTitle, setBreadcrumbTitle] = useState('Edit Filing'); // Updated when company info loads
  const { companyId, filingId } = useParams();
  const { selectedCompany, setSelectedCompany, userCompanies } = useCompany();
  const { user } = useSession();
  
  // Dynamic data from database/S3 - Use refs to avoid re-renders
  const formStructureRef = useRef<any>(null);
  const companyInfoLoadedRef = useRef<any>(null);
  const filingOrgIdRef = useRef<string | null>(null); // Store the filing's actual org_id
  const negativeTargetsRef = useRef<string[]>([]);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [metadataLoaded, setMetadataLoaded] = useState(false); // Trigger once when S3+DB complete
  const [filingCountryIso, setFilingCountryIso] = useState<string>('MY');

  // Footnote modal - refs only (NO STATE)
  const currentFootnoteFieldRef = useRef<{
    fieldId: string;
    fieldLabel: string;
    fieldName: string;
    contextRef: string;
    qname: string;
    currentYearContext: string;
    comparativeYearContext: string;
    currentYear: string;
    comparativeYear: string;
    memberType: string;
    isReadonly: boolean;
  } | null>(null);
  
  // Save state - NO React state (was causing full-tree re-renders on every save
  // for consolidated filings, 12k+ inputs reconciled per state change).
  // Instead: saveStateBus dispatches DOM events, Save button listens via ref,
  // and saveSuccessModalBus controls the always-mounted SaveSuccessModal.
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const saveBtnIdleRef = useRef<HTMLSpanElement>(null);
  const saveBtnBusyRef = useRef<HTMLSpanElement>(null);
  
  // Track unsaved changes (simple boolean flag - no data comparison needed)
  const hasUnsavedChangesRef = useRef(false);

  // Callback for HyperFormula to read from ref (single source of truth)
  // Note: HyperFormula uses standard contexts (current_SeparateMember), but we need to find the real context ref
  const getFieldValueFromRef = useCallback((fieldName: string, standardContext: string): string => {
    // HyperFormula uses standard context, but our ref uses real context ref
    // Find input with this standard context to get the real context ref
    const input = document.querySelector(
      `input[name="${fieldName}"][data-year-end="${standardContext}"]`
    ) as HTMLInputElement;

    if (!input) {
      return '';
    }

    const realContextRef = input.getAttribute('data-context-ref');
    const qname = input.getAttribute('data-qname');

    if (!realContextRef || !qname) {
      return '';
    }

    // Ref key format: "qname_realContextRef" (matches database and form)
    const key = `${qname}_${realContextRef}`;
    return formDataRef.current[key] || '';
  }, []);

  // Callback for HyperFormula to update ref when calculating (then update DOM)
  // Note: HyperFormula uses standard contexts, but we store using real context ref
  const updateRefAndDOM = useCallback((fieldName: string, standardContext: string, value: string) => {
    // Find input to get real context ref and qname
    const input = document.querySelector(
      `input[name="${fieldName}"][data-year-end="${standardContext}"]`
    ) as HTMLInputElement;

    if (!input) {
      return;
    }

    const realContextRef = input.getAttribute('data-context-ref');
    const qname = input.getAttribute('data-qname');

    if (!realContextRef || !qname) {
      return;
    }

    // 1. Update ref (single source of truth) - use real context ref (matches database)
    const key = `${qname}_${realContextRef}`;
    formDataRef.current[key] = value;

    // ALSO store using fieldName_standardContext format for SOFPTable compatibility
    const sofpTableRefKey = `${fieldName}_${standardContext}`;
    formDataRef.current[sofpTableRefKey] = value;

    // 2. Update DOM to mirror ref (UI layer)
    document.querySelectorAll(
      `input[name="${fieldName}"][data-year-end="${standardContext}"]`
    ).forEach((element) => {
      const input = element as HTMLInputElement;
      input.value = value;
      input.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
        detail: { fieldName, contextRef: realContextRef, value }
      }));
    });

    // 3. If this is IncreaseDecreaseInCashAndCashEquivalents, dispatch event to trigger closing cash calculation
    // Use setTimeout to ensure HyperFormula update cycle completes before ClosingCash recalculates
    if (fieldName === 'IncreaseDecreaseInCashAndCashEquivalents') {
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('increaseDecreaseCashUpdated', {
          detail: { fieldName, standardContext, realContextRef, value, qname }
        }));
      }, 0);
    }
  }, []);

  // Initialize HyperFormula with ref callbacks - Read from ref (no re-render dependency)
  const hyperFormula = useHyperFormula(formStructureRef.current, 'SOFP', getFieldValueFromRef, updateRefAndDOM);
  const navigate = useNavigate();
  const dataLoadStartedRef = useRef(false); // Prevent multiple data loads
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Store timeout to prevent cleanup

  // Country-first validation routing (MY vs SG) using database country_iso.
  const resolvedCountryIso = (filingCountryIso || 'MY').trim().toUpperCase();
  const malaysiaValidation = useValidation(formDataRef, { enabled: resolvedCountryIso === 'MY' });
  const singaporeValidation = useValidationSG(formDataRef, { enabled: resolvedCountryIso === 'SG' });
  const activeValidation = useMemo(() => {
    return resolvedCountryIso === 'SG' ? singaporeValidation : malaysiaValidation;
  }, [resolvedCountryIso, malaysiaValidation, singaporeValidation]);

  const {
    errors,
    hasErrors,
    checkValidation,
    clearErrors,
    updateFieldValue,
    getSectionErrorCount,
    hasFieldError,
    getFieldError
  } = activeValidation;
  
  // Cross-field navigation (matches 1.0)
  const crossFieldNav = useCrossFieldNavigation();
  
  // Scope filter (conditional field visibility - matches 1.0 scopeFilter.js)
  const scopeFilter = useScopeFilter();


  // STEP 1: Load taxonomy structure from S3 and ALL filing data from database
  // This MUST complete before anything else
  useEffect(() => {
    const loadMetadata = async () => {
      if (!filingId) {
        setLoadingError('No filing ID provided');
        return;
      }
      
      try {
        devLog('🚀 [STEP 1] Loading filing data (company_info, org_id, form_data)...');
        
        // Get EVERYTHING from filings table in ONE query
        const { data: filingData, error: filingError } = await supabase
          .from('filings')
          .select('company_info, org_id, form_data, country_iso')
          .eq('id', filingId)
          .single();
        
        if (filingError || !filingData) {
          throw new Error(`Failed to load filing: ${filingError?.message}`);
        }
        
        if (!filingData.org_id) {
          throw new Error('Filing has no org_id');
        }
        
        if (!filingData.company_info) {
          throw new Error('Filing has no company_info');
        }
        
        // Set filing country FIRST so render/validation routing is stable.
        setFilingCountryIso((filingData.country_iso || 'MY').toUpperCase());

        // Store org_id for PDF viewer (admin viewing other org's files)
        filingOrgIdRef.current = filingData.org_id;
        
        // Store form_data immediately (avoid second query later)
        if (filingData.form_data) {
          supabaseDataRef.current = { form_data: filingData.form_data };
          devLog('✅ [STEP 1] Form data cached from initial query');
        }
        
        // STEP 1A: Load form structure from S3 (REQUIRED) - Store in ref (no re-render)
        // 🔧 TEMP: Set to true to use local JSON file for testing instead of S3
        const USE_LOCAL_TAXONOMY = false;
        
        let taxonomy;
        if (USE_LOCAL_TAXONOMY) {
          devLog('📥 [STEP 1A] Using LOCAL form_structure_custom.json (testing mode)...');
          taxonomy = formStructureCustom;
        } else {
          devLog('📥 [STEP 1A] Loading form structure from S3...');
          taxonomy = await getTaxonomyFromS3(filingData.org_id, filingId);
        }
        formStructureRef.current = taxonomy;
        devLog('✅ [STEP 1A] Form structure loaded → ref');
        
        // Find negative weight targets (fields subtracted from totals)
        const negativeTargets = findNegativeWeightTargets(taxonomy);
        negativeTargetsRef.current = negativeTargets;
        devLog('✅ [STEP 1A] Found negative targets:', negativeTargets);
        
        // STEP 1B: Set company info (REQUIRED) - Store in ref (no re-render)
        companyInfoLoadedRef.current = filingData.company_info;
        devLog('✅ [STEP 1B] Company info loaded → ref');
        
        // Update breadcrumb with company name and year end
        const companyName = filingData.company_info.company_name || 'Unknown Company';
        const yearEnd = filingData.company_info.currentPeriod?.end || '';
        
        if (yearEnd) {
          // Format YYYYMMDD to "DD MMM YYYY"
          const year = yearEnd.substring(0, 4);
          const month = yearEnd.substring(4, 6);
          const day = yearEnd.substring(6, 8);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthName = monthNames[parseInt(month) - 1] || '';
          const formattedDate = `${day} ${monthName} ${year}`;
          
          setBreadcrumbTitle(`${companyName} • ${formattedDate}`);
        } else {
          setBreadcrumbTitle(companyName);
        }
        
        devLog('✅ [STEP 1] Metadata complete, triggering ONE render');
        setMetadataLoaded(true); // Trigger ONE state update to re-render with data
        
      } catch (error: any) {
        console.error('❌ [STEP 1] FAILED:', error.message);
        setLoadingError(error.message || 'Failed to load filing metadata');
      }
    };
    
    loadMetadata();
  }, [filingId]);

  useEffect(() => {
    loadMSICCodes().catch((err) => {
      console.warn('MSIC preload failed:', err);
    });
  }, []);

  // Coordinator: Wait for ALL HyperFormula instances to complete DURING INITIAL LOAD ONLY
  useEffect(() => {
    if (!metadataLoaded) return;
    const initialLoadCompleteRef = { current: false };

    // Determine which completion events are actually expected for THIS filing.
    // Some filings may not have SOCIE or Note tables, so waiting for them would deadlock the loader.
    // IMPORTANT: We do NOT wait for SOCIE anymore (per UX decision).
    // SOCIE is optional and can cause the loader to deadlock if its event is delayed/missed.
    const expected: Array<'sofp' | 'note'> = ['sofp'];

    const hasAnyFieldName = (node: any, names: Set<string>): boolean => {
      if (!node) return false;
      if (typeof node.name === 'string' && names.has(node.name)) return true;
      const children = node.structure?.children;
      if (Array.isArray(children)) {
        for (const child of children) {
          if (hasAnyFieldName(child, names)) return true;
        }
      }
      return false;
    };

    const hasAnyInForm = (names: Set<string>): boolean => {
      const sectionsObj = formStructureRef.current?.sections;
      if (!sectionsObj) return false;
      for (const section of Object.values(sectionsObj)) {
        if (hasAnyFieldName(section, names)) return true;
      }
      return false;
    };

    const hasNoteHF = hasAnyInForm(new Set([
      'DisclosureOfClassesOfShareCapitalAbstract',
      'DisclosureOfTransactionsBetweenRelatedPartiesAbstract',
    ]));

    if (hasNoteHF) expected.push('note');
    
    const handleAllCalculationsComplete = () => {
      // Only trigger validation once during initial load
      if (initialLoadCompleteRef.current) return;
      initialLoadCompleteRef.current = true;
      
      devLog('✅ [COORDINATOR] All HyperFormulas complete, triggering validation ONCE');
      
      // Now that we wait for hyperformulaCalculationsComplete before calculating special fields,
      // we can use a shorter delay
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('specialFieldsCalculated'));
        
        // Close loading screen AFTER validation completes
        setTimeout(() => {
          devLog('✅ [COORDINATOR] Closing loading screen after validation');
          setIsLoadingData(false);
          setIsFormReady(true);
          
          // Initialize scope filters (BEFORE cross-field nav)
          setTimeout(() => {
            devLog('🔍 Initializing scope filters...');
            scopeFilter.initializeScopeFilters();
          }, 300);
          
          // Initialize cross-field navigation
          setTimeout(() => {
            devLog('🔗 Initializing cross-field navigation...');
            crossFieldNav.scanFields();
          }, 500);
        }, 500);
      }, 300);
    };

    // Track which HyperFormulas have completed
    const completedHyperFormulas = new Set<string>();

    const isAllExpectedComplete = () => expected.every((k) => completedHyperFormulas.has(k));
    
    const handleSOFPComplete = () => {
      if (initialLoadCompleteRef.current) return; // Ignore after initial load
      completedHyperFormulas.add('sofp');
      devLog(`✅ [COORDINATOR] SOFP complete (${completedHyperFormulas.size}/${expected.length})`, { expected });
      if (isAllExpectedComplete()) {
        handleAllCalculationsComplete();
      }
    };

    const handleNoteComplete = () => {
      if (initialLoadCompleteRef.current) return; // Ignore after initial load
      completedHyperFormulas.add('note');
      devLog(`✅ [COORDINATOR] Note complete (${completedHyperFormulas.size}/${expected.length})`, { expected });
      if (isAllExpectedComplete()) {
        handleAllCalculationsComplete();
      }
    };

    document.addEventListener('sofpCalculationsComplete', handleSOFPComplete);
    document.addEventListener('noteCalculationsComplete', handleNoteComplete);

    // Fallback: never deadlock the loader if the SOFP event is missed.
    // (This can happen if an event dispatch occurs before listeners attach, or if a table isn't mounted for some reason.)
    const sofpFallbackTimeoutId = window.setTimeout(() => {
      if (initialLoadCompleteRef.current) return;
      if (!completedHyperFormulas.has('sofp')) {
        completedHyperFormulas.add('sofp');
        console.warn(`⚠️ [COORDINATOR] SOFP event not received - bypassing after timeout (${completedHyperFormulas.size}/${expected.length})`, { expected });
        if (isAllExpectedComplete()) {
          handleAllCalculationsComplete();
        }
      }
    }, 4000);

    return () => {
      document.removeEventListener('sofpCalculationsComplete', handleSOFPComplete);
      document.removeEventListener('noteCalculationsComplete', handleNoteComplete);
      window.clearTimeout(sofpFallbackTimeoutId);
    };
  }, [metadataLoaded]);

  // Set page title
  useEffect(() => {
    document.title = 'Rapid Tagger | Edit Filing';
    return () => {
      document.title = 'Rapid Tagger';
    };
  }, []);
  
  // Check if running in Electron
  useEffect(() => {
    // @ts-ignore
    const isElectron = window.electronAPI !== undefined;
    if (isElectron) {
      devLog('Running in Electron environment');
    }
  }, []);

  useEffect(() => {
    if (companyId && selectedCompany?.company_id !== companyId) {
      // Find the company in userCompanies and set it as selected
      const company = userCompanies.find(c => c.company_id === companyId);
      if (company) {
        setSelectedCompany(company);
      }
    }
  }, [companyId, selectedCompany, userCompanies, setSelectedCompany]);

  // Load form data from database format into ref (single source of truth)
  // Database format: { "realContext": { "qname": { "value": "...", "metadata": {...} } } }
  // Ref format: { "qname_realContext": "value" } - 100% matches database format
  // 
  // APPROACH: Update ref first, then mirror to DOM
  // 1. Find input by data-context-ref + data-qname (real context + qname from database)
  // 2. Store in ref using "qname_realContext" format (matches database and FormField)
  // 3. Update DOM to mirror ref (UI layer)
  // 4. Push to HyperFormula (HyperFormula uses standard contexts, but we store using real contexts)
  // Load form data into useRef (NOT useState - useRef is single source of truth)
  // Updates formDataRef.current using real context refs (matches database 100%)
  const loadFormDataIntoState = useCallback((databaseData: Record<string, Record<string, any>>) => {
    devLog('📥 [QUEUE STEP 2] loadFormDataIntoRef called (updating useRef with real context refs)...');
    const refData: Record<string, string> = {};

    // PERF: Build O(1) lookup maps for DOM elements and footnote anchors.
    const elementMap = new Map<string, Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>>();
    const allFormElements = document.querySelectorAll(
      'input[data-context-ref][data-qname], select[data-context-ref][data-qname], textarea[data-context-ref][data-qname]'
    ) as NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

    allFormElements.forEach((el) => {
      const ctx = el.getAttribute('data-context-ref');
      const qn = el.getAttribute('data-qname');
      if (!ctx || !qn) return;
      const key = `${qn}_${ctx}`;
      const arr = elementMap.get(key);
      if (arr) arr.push(el);
      else elementMap.set(key, [el]);
    });

    const footnoteAnchorMap = new Map<string, HTMLElement[]>();
    const allFootnoteAnchors = document.querySelectorAll('.footnote-anchor[data-context-ref][data-qname]') as NodeListOf<HTMLElement>;
    allFootnoteAnchors.forEach((anchor) => {
      const ctx = anchor.getAttribute('data-context-ref');
      const qn = anchor.getAttribute('data-qname');
      if (!ctx || !qn) return;
      const key = `${ctx}_${qn}`;
      const arr = footnoteAnchorMap.get(key);
      if (arr) arr.push(anchor);
      else footnoteAnchorMap.set(key, [anchor]);
    });

    // Process all data: Update ref → Update DOM → Collect monetary values for HyperFormula
    Object.entries(databaseData).forEach(([realContext, fields]) => {
      Object.entries(fields).forEach(([qname, fieldData]: [string, any]) => {
        // Ensure value is always a string
        let value = fieldData?.value;
        if (value === null || value === undefined || value === '') {
          return;
        }
        // Convert to string if it's a number
        value = typeof value === 'string' ? value : String(value);

        // CORE LOGIC: Match by data-context-ref + data-qname pair (fast via prebuilt map)
        const refKey = `${qname}_${realContext}`;
        const elements = elementMap.get(refKey);
        if (!elements || elements.length === 0) {
          // Element not found - might be a calculated field without qname (fieldOccurrence === 2)
          // This is expected and should be skipped (not saved to database)
          // console.warn(`⚠️ Form element not found for ${qname} with context ${realContext} - skipping (likely calculated field without qname)`);
          return;
        }

        // Store using real context ref (matches database format 100%)
        formDataRef.current[refKey] = value;
        refData[refKey] = value;

        // ALSO store using fieldName_standardContext format for SOFPTable compatibility
        // Use the FIRST element (they all share same name/year-end)
        const firstEl = elements[0];
        const fieldName = firstEl.getAttribute('name');
        const standardContext = firstEl.getAttribute('data-year-end');
        if (fieldName && standardContext) {
          const sofpTableRefKey = `${fieldName}_${standardContext}`;
          formDataRef.current[sofpTableRefKey] = value;
          refData[sofpTableRefKey] = value;
        }

        // 2. Update DOM to mirror ref (UI layer)
        // PERF: Set raw values only here; initial formatting runs once later.
        elements.forEach((el) => {
          el.value = value;
        });

        // 3. Load footnote data if present in metadata (apply to anchors; DOM manager renders buttons)
        if (fieldData?.metadata?.footnote || fieldData?.metadata?.breakdown) {
          const anchorKey = `${realContext}_${qname}`;
          const footnoteAnchors = footnoteAnchorMap.get(anchorKey) || [];
          footnoteAnchors.forEach(anchor => {
            if (fieldData.metadata.footnote) {
              anchor.setAttribute('data-footnote-text', fieldData.metadata.footnote);
            } else {
              anchor.removeAttribute('data-footnote-text');
            }

            if (fieldData.metadata.breakdown && Array.isArray(fieldData.metadata.breakdown)) {
              anchor.setAttribute('data-footnote-breakdown', JSON.stringify(fieldData.metadata.breakdown));
            } else {
              anchor.removeAttribute('data-footnote-breakdown');
            }
          });
        }
      });
    });

    // console.log(`✅ [QUEUE STEP 2] Updated ${Object.keys(refData).length} fields in useRef`);

    // Format ALL financial inputs ONCE after initial load (matches 1.0 main.js lines 107-120, 250-253)
    // This runs ONLY during initial data load, never again
    setTimeout(() => {
      const financialInputs = document.querySelectorAll('.financial-input');
      financialInputs.forEach(input => {
        if (input instanceof HTMLInputElement && input.value && input.value.trim() !== '') {
          // CRITICAL: Skip if input is currently focused (user might be typing)
          if (document.activeElement === input) return;
          
          // Format with numeral.js (only if not already formatted)
          if (!input.value.includes(',')) {
            const numValue = (window as any).numeral ? (window as any).numeral(input.value).value() : parseFloat(input.value.replace(/,/g, ''));
            if (!isNaN(numValue)) {
              if ((window as any).numeral) {
                input.value = (window as any).numeral(numValue).format('0,0.00');
              } else {
                input.value = numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              }
            }
          }
        }
      });
      devLog('✅ Initial formatting complete');
      
      // Initialize XBRL mandatory fields with 0.00 if empty (matches 1.0 XBRLFieldHandler)
      initializeXBRLMandatoryFields();
      
      // Apply read-only to partner-filled fields (matches 1.0 readonly.js)
      applyReadOnlyFields();

      // Refresh footnote anchors (DOM-managed buttons/indicators)
      refreshFootnoteAnchors({
        onMarkDirty: () => {
          hasUnsavedChangesRef.current = true;
        }
      });

      updateNoteOtherEmptyWarning();
      
      // Reset unsaved changes flag after initial load
      hasUnsavedChangesRef.current = false;
      devLog('✅ Initial form data loaded, unsaved changes flag reset');
    }, 200);

    // Return Promise that resolves after HyperFormula hooks have a chance to sync (via formDataLoaded).
    return new Promise<void>((resolve) => {
      // Always dispatch once: each HyperFormula hook will sync from ref/DOM and then
      // coordinator will close the loader after receiving their completion events.
      document.dispatchEvent(new CustomEvent('formDataLoaded'));
      devLog('✅ [QUEUE STEP 2] Dispatched formDataLoaded for HyperFormula sync');
      // Resolve immediately; coordinator manages final readiness timing.
      resolve();
    });
  }, [hyperFormula]);

  // Cached Supabase data (loaded in STEP 1 metadata load)
  const supabaseDataRef = useRef<any>(null);

  // STEP 3: Load form data from database
  // This runs AFTER formStructure is loaded and form inputs are rendered
  useEffect(() => {
    devLog('🔍 [STEP 3] Data loading useEffect triggered', {
      metadataLoaded,
      hyperFormulaReady: hyperFormula.isReady,
      isLoadingData,
      dataLoadStarted: dataLoadStartedRef.current,
      filingId,
      hasInputs: document.querySelectorAll('input[data-year-end]').length > 0
    });

    // Wait for metadata to load first
    if (!metadataLoaded) {
      devLog('⏸️ [STEP 3] Waiting for metadata...');
      return;
    }

    // Only run when HyperFormula is ready and form inputs are rendered
    if (!hyperFormula.isReady) {
      devLog('⏸️ [STEP 3] Waiting for HyperFormula...');
      return;
    }

    // Prevent multiple loads - use ref to track if we've already started
    if (dataLoadStartedRef.current) {
      devLog('⏸️ [QUEUE STEP 0] Skipping - Data load already started');
      return;
    }

    // Check if form inputs are rendered (SOFP tables use data-year-end, FormField uses data-context-ref)
    const hasInputs = document.querySelectorAll('input[data-year-end], input[data-context-ref], select[data-context-ref], textarea[data-context-ref]').length > 0;
    if (!hasInputs) {
      devLog('⏸️ [QUEUE STEP 0] Skipping - Form inputs not rendered yet');
      return;
    }

    devLog('✅ [QUEUE STEP 0] Starting data load process...');
    // Mark as started IMMEDIATELY before anything else to prevent cleanup
    dataLoadStartedRef.current = true;
    setIsLoadingData(true);

    // Store loadFormDataIntoState in a ref to avoid dependency issues
    const loadFormDataFn = loadFormDataIntoState;

    // Proper async/await function to load data sequentially
    const loadFilingDataAsync = async () => {
      try {
        devLog('🚀 [QUEUE STEP 1] Entering async function...');

        // If no filingId, nothing to load - close immediately
        if (!filingId) {
          devLog('ℹ️ [QUEUE STEP 1] No filingId - new filing, closing loading screen');
          setIsLoadingData(false);
          setIsFormReady(true);
          setTimeout(() => updateNoteOtherEmptyWarning(), 0);
          return;
        }

        // Step 1: Use cached form_data (loaded in STEP 1 with company_info)
        let filingFormsData: any;

        if (supabaseDataRef.current) {
          devLog('✅ [QUEUE STEP 1] Using cached form_data (from initial query)');
          filingFormsData = supabaseDataRef.current;
        } else {
          devLog('⚠️ [QUEUE STEP 1] No cached data - empty form');
          filingFormsData = null;
        }

        // Step 2: Load into useRef → DOM → HyperFormula initialization
        if (filingFormsData?.form_data) {
          devLog('📥 [QUEUE STEP 2] Loading into useRef and initializing HyperFormula...');
          await loadFormDataFn(filingFormsData.form_data);
          // Note: loadFormDataIntoState already logs completion internally
        } else {
          devLog('ℹ️ [QUEUE STEP 2] No data - empty form');
          // No data - close loading screen immediately
          setIsLoadingData(false);
          setIsFormReady(true);
          setTimeout(() => updateNoteOtherEmptyWarning(), 0);
        }

        // Step 3: Wait for coordinator to close loading screen
        // (Coordinator will close after all HyperFormulas complete + validation runs)
        devLog('⏳ [QUEUE STEP 3] Waiting for coordinator to close loading screen...');

      } catch (error) {
        console.error('❌ [QUEUE ERROR] Error in data loading sequence:', error);
        setIsLoadingData(false);
        setIsFormReady(true);
      }
    };

    // No delay needed - inputs are already rendered (checked above)
    devLog('🚀 [QUEUE STEP 0] Starting data load immediately (inputs ready)...');

    // Clear any existing timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Call immediately - no delay needed
    loadFilingDataAsync();

    // Don't clean up in return - let the timeout complete
    // The dataLoadStartedRef prevents re-running anyway
    return () => {
      // CRITICAL: Check CURRENT ref value (refs are mutable, so this sees the latest value)
      // If load has started, NEVER clean up - let the timeout fire
      if (dataLoadStartedRef.current) {
        devLog('🧹 [QUEUE CLEANUP] Load already started - NOT cleaning up timeout, letting it fire');
        return; // Don't clean up - let the timeout complete
      }

      // Only cleanup if load hasn't started AND timeout exists
      if (timeoutRef.current) {
        devLog('🧹 [QUEUE CLEANUP] Cleaning up timeout (load not started yet)');
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      } else {
        devLog('🧹 [QUEUE CLEANUP] No timeout to clean up');
      }
    };
  }, [hyperFormula.isReady, filingId, metadataLoaded]); // Wait for metadata before loading form data

  // Prepare sections for tabs - Read from ref (only compute once when metadataLoaded changes)
  const sections = useMemo(() => {
    if (!formStructureRef.current) return [];
    
    return Object.entries(formStructureRef.current.sections).map(([code, data]: [string, any]) => {
      // Apply sectionConfig mapping (same as FormSectionTabs)
      const config = sectionConfig[code] || {
        display_name: data.standard_label?.replace('[abstract]', '')?.trim() || code,
        category: 'Other'
      };
      
      return {
        code,
        name: config.display_name,
        category: config.category,
        data
      };
    });
  }, [metadataLoaded]); // Only re-compute when metadata first loads

  // Set initial active section (direct DOM, no state)
  useEffect(() => {
    if (sections.length > 0 && !activeSectionRef.current) {
      activeSectionRef.current = sections[0].code;
      // Show first section
      setTimeout(() => {
        handleSectionChange(sections[0].code);
      }, 0);
    }
  }, [sections]);


  // Recursion guard to prevent infinite loops
  const isHandlingChangeRef = useRef(false);
  
  // Handle field change - update ref first (single source of truth), then HyperFormula
  const handleFieldChange = useCallback((fieldName: string, contextRef: string, value: string, qname?: string) => {
    // Guard against infinite recursion
    if (isHandlingChangeRef.current) {
      return;
    }
    
    isHandlingChangeRef.current = true;
    let standardContextForPostUpdate: string | null = null;
    let shouldTriggerClosingCashRecalc = false;
    
    try {
      // Simple 3-step flow: DOM → ref → validation
      
      // Find the input
      const input = document.querySelector(
        `input[name="${fieldName}"][data-context-ref="${contextRef}"], 
         select[name="${fieldName}"][data-context-ref="${contextRef}"], 
         textarea[name="${fieldName}"][data-context-ref="${contextRef}"]`
      ) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

      if (!input) {
        console.warn(`Input not found for ${fieldName}_${contextRef}`);
        return;
      }

      const standardContext = input.getAttribute('data-year-end');
      const inputQname = input.getAttribute('data-qname') || qname;
      standardContextForPostUpdate = standardContext;

      // Special calculated fields (SOPL-, SOCI-, ClosingCash-, SORE-Closing-, SORE-Opening-, RetainedEarningsRestated) - skip database storage
      const isSpecialCalculatedField = fieldName.startsWith('SOPL-') || 
                                       fieldName.startsWith('SOCI-') || 
                                       fieldName.startsWith('ClosingCash-') ||
                                       fieldName.startsWith('SORE-Closing-') ||
                                       fieldName.startsWith('SORE-Opening-') ||
                                       fieldName === 'RetainedEarningsRestated';

      if (isSpecialCalculatedField) {
        if (standardContext) {
          const sofpTableRefKey = `${fieldName}_${standardContext}`;
          if (value === '' || value.trim() === '') {
            delete formDataRef.current[sofpTableRefKey];
          } else {
            formDataRef.current[sofpTableRefKey] = value;
          }
        }
        return;
      }

      if (!inputQname) {
        console.warn(`Missing qname for ${fieldName}_${contextRef}`);
        return;
      }

      // 1. Update DOM
      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
        input.value = value;
      } else if (input instanceof HTMLSelectElement) {
        input.value = value;
      }

      // 2. Update ref
      const refKey = `${inputQname}_${contextRef}`;
      if (value === '' || value.trim() === '') {
        delete formDataRef.current[refKey];
        if (standardContext) {
          delete formDataRef.current[`${fieldName}_${standardContext}`];
        }
      } else {
        formDataRef.current[refKey] = value;
        if (standardContext) {
          formDataRef.current[`${fieldName}_${standardContext}`] = value;
        }
      }

      // Mark as dirty (has unsaved changes)
      hasUnsavedChangesRef.current = true;

      // Update HyperFormula (global SOFP/SORE tables)
      if (hyperFormula.handleFieldChange && standardContext) {
        hyperFormula.handleFieldChange(fieldName, standardContext, value);
      }

      // ✅ FIX: Dispatch input event for local HyperFormula instances (Note tables)
      // Note tables (useHyperFormulaNote) listen for input events to trigger recalculation
      // Check if this is a Note table input (has data-note-member attribute)
      if (input instanceof HTMLInputElement && input.hasAttribute('data-note-member')) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // If OpeningCash changes (or IncreaseDecrease is directly updated), we need to recalc ClosingCash.
      // IMPORTANT: SOFPTable listens to `increaseDecreaseCashUpdated` and will call updateClosingCash(),
      // which calls back into this handleFieldChange for ClosingCash-* fields.
      // So we must dispatch this AFTER the recursion guard resets (see finally below).
      if (standardContext && (fieldName.startsWith('OpeningCash-') || fieldName === 'IncreaseDecreaseInCashAndCashEquivalents')) {
        shouldTriggerClosingCashRecalc = true;
      }

      // 3. Trigger validation (DON'T dispatch change - causes infinite loop!)
      // Validation will run automatically via useValidation hook watching formDataRef
      // input.dispatchEvent(new Event('change', { bubbles: true }));
      
    } finally {
      // Reset guard
      isHandlingChangeRef.current = false;

      // Post-update: trigger ClosingCash recalculation for the affected column.
      // Use a microtask so SOFPTable can safely call back into handleFieldChange.
      if (shouldTriggerClosingCashRecalc && standardContextForPostUpdate) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('increaseDecreaseCashUpdated', {
            detail: { standardContext: standardContextForPostUpdate }
          }));
        }, 0);
      }
    }
  }, [hyperFormula]);

  useDirectorCrossSectionSuggest(
    resolvedCountryIso === 'MY',
    handleFieldChange,
    formDataRef
  );

  const handleSectionChange = (sectionCode: string) => {
    // Pure DOM manipulation (no re-render) - instant like 1.0
    activeSectionRef.current = sectionCode;
    
    // Fire event for FormSectionTabs to listen to (NO STATE, NO RE-RENDERS)
    document.dispatchEvent(new CustomEvent('sectionChanged', { 
      detail: { sectionCode } 
    }));

    // Update tab classes
    document.querySelectorAll('.sub-panel-item').forEach(el => {
      el.classList.remove('sub-panel-active');
    });
    document.querySelector(`[data-tab="${sectionCode}"]`)?.parentElement?.classList.add('sub-panel-active');
    
    // Show/hide sections
    document.querySelectorAll('.sub-content-item').forEach(el => {
      if (el.id === sectionCode) {
        el.classList.add('sub-content-active');
        (el as HTMLElement).style.display = 'block';
      } else {
        el.classList.remove('sub-content-active');
        (el as HTMLElement).style.display = 'none';
      }
    });
    
    // Update dropdown text imperatively (no re-render)
    const dropdownTextEl = document.querySelector('.section-dropdown-text') as HTMLElement | null;
    if (dropdownTextEl) {
      const sectionEl = document.getElementById(sectionCode);
      const displayName = sectionEl?.getAttribute('data-section-name') || sectionCode;
      dropdownTextEl.textContent = displayName;
    }

    updateNoteOtherEmptyWarning();
  };

  // Listen for cross-field navigation requests (from useCrossFieldNavigation in PDF mode)
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const sectionId = customEvent.detail?.sectionId;
      if (sectionId) {
        handleSectionChange(sectionId);
      }
    };
    
    document.addEventListener('crossFieldNavigate', handler);
    return () => document.removeEventListener('crossFieldNavigate', handler);
  }, []);

  // Listen for year-end modal open request
  useEffect(() => {
    const handler = () => {
      // Extract current year-end dates from form
      const currentStartInput = document.querySelector('input[name="CompanyCurrentFinancialYearStartDate"]') as HTMLInputElement;
      const currentEndInput = document.querySelector('input[name="CompanyCurrentFinancialYearEndDate"]') as HTMLInputElement;
      const previousStartInput = document.querySelector('input[name="CompanyPreviousFinancialYearStartDate"]') as HTMLInputElement;
      const previousEndInput = document.querySelector('input[name="CompanyPreviousFinancialYearEndDate"]') as HTMLInputElement;
      
      // Determine first year from company info
      const firstYear = companyInfoLoadedRef.current?.first_year || 'N';

      setYearEndModalContext({
        currentStart: currentStartInput?.value || '',
        currentEnd: currentEndInput?.value || '',
        previousStart: previousStartInput?.value || '',
        previousEnd: previousEndInput?.value || '',
        firstYear: firstYear,
        filingId: filingId || '',
        orgId: filingOrgIdRef.current || selectedCompany?.company_id || '',
        onSuccess: () => {
          // Clear unsaved changes flag and redirect
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });

      openYearEndModal();
    };

    document.addEventListener('openYearEndModal', handler);
    return () => document.removeEventListener('openYearEndModal', handler);
  }, [filingId, selectedCompany?.company_id, navigate]);

  // Listen for expenses method change modal open request
  useEffect(() => {
    const handler = () => {
      // Extract current method from form
      const methodSelect = document.querySelector('select[name="MethodUsedForPreparingStatementOfProfitOrLoss"]') as HTMLSelectElement;
      const currentMethod = methodSelect?.value || '';

      if (!currentMethod) {
        alert('Please select an expenses method first.');
        return;
      }

      setExpensesMethodChangeModalContext({
        currentMethod: currentMethod,
        filingId: filingId || '',
        orgId: filingOrgIdRef.current || selectedCompany?.company_id || '',
        onSuccess: () => {
          // Clear unsaved changes flag and redirect
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });

      openExpensesMethodChangeModal();
    };

    document.addEventListener('openExpensesMethodChangeModal', handler);
    return () => document.removeEventListener('openExpensesMethodChangeModal', handler);
  }, [filingId, selectedCompany?.company_id, navigate]);

  // Listen for equity method change modal open request
  useEffect(() => {
    const handler = () => {
      // Extract current method from form
      const methodSelect = document.querySelector('select[name="MethodUsedForRepresentingChangesInAnEntitysEquity"]') as HTMLSelectElement;
      const currentMethod = methodSelect?.value || '';

      if (!currentMethod) {
        alert('Please select an equity method first.');
        return;
      }

      setEquityMethodChangeModalContext({
        currentMethod: currentMethod,
        filingId: filingId || '',
        orgId: filingOrgIdRef.current || selectedCompany?.company_id || '',
        onSuccess: () => {
          // Clear unsaved changes flag and redirect
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });

      openEquityMethodChangeModal();
    };

    document.addEventListener('openEquityMethodChangeModal', handler);
    return () => document.removeEventListener('openEquityMethodChangeModal', handler);
  }, [filingId, selectedCompany?.company_id, navigate]);

  // SOFP (Statement of Financial Position) method change
  useEffect(() => {
    const handler = () => {
      const methodSelect = document.querySelector(
        'select[name="MethodUsedForPreparingStatementOfFinancialPosition"]'
      ) as HTMLSelectElement;
      const currentMethod = methodSelect?.value || '';

      if (!currentMethod) {
        alert('Please select a financial position method first.');
        return;
      }

      setSofpMethodChangeModalContext({
        currentMethod,
        filingId: filingId || '',
        orgId: filingOrgIdRef.current || selectedCompany?.company_id || '',
        onSuccess: () => {
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });

      openSofpMethodChangeModal();
    };

    document.addEventListener('openSofpMethodChangeModal', handler);
    return () => document.removeEventListener('openSofpMethodChangeModal', handler);
  }, [filingId, selectedCompany?.company_id, navigate]);

  // Statement of Cash Flows method change
  useEffect(() => {
    const handler = () => {
      const methodSelect = document.querySelector(
        'select[name="MethodUsedForPreparingStatementOfCashFlows"]'
      ) as HTMLSelectElement;
      const currentMethod = methodSelect?.value || '';

      if (!currentMethod) {
        alert('Please select a cash flows method first.');
        return;
      }

      setCashFlowsMethodChangeModalContext({
        currentMethod,
        filingId: filingId || '',
        orgId: filingOrgIdRef.current || selectedCompany?.company_id || '',
        onSuccess: () => {
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });

      openCashFlowsMethodChangeModal();
    };

    document.addEventListener('openCashFlowsMethodChangeModal', handler);
    return () => document.removeEventListener('openCashFlowsMethodChangeModal', handler);
  }, [filingId, selectedCompany?.company_id, navigate]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but setting returnValue triggers the dialog
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Block React Router navigation if there are unsaved changes
  // Intercept ALL link clicks BEFORE React Router handles them (capture phase)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Only check if there are unsaved changes
      if (!hasUnsavedChangesRef.current) return;

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      
      if (!link) return;

      // Check if it's an internal navigation (React Router link)
      const href = link.getAttribute('href') || '';
      const currentPath = window.location.pathname;
      
      // Skip if same page, external link, or hash link
      if (href === currentPath || 
          href.startsWith('http://') || 
          href.startsWith('https://') ||
          href.startsWith('#')) {
        return;
      }

      // This is an internal navigation - check for unsaved changes
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave this page?\n\nAll unsaved changes will be lost.'
      );

      if (!confirmLeave) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      } else {
        // User confirmed - clear the flag so they can leave
        hasUnsavedChangesRef.current = false;
      }
    };

    // Use capture phase to intercept BEFORE React Router's event handlers
    document.addEventListener('click', handleClick, true);
    
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  // Helper to extract year from context ref
  const extractYearFromContext = (contextRef: string): string => {
    if (contextRef.startsWith('fromto_')) {
      const parts = contextRef.split('_');
      if (parts.length >= 3) {
        return parts[2].substring(0, 4); // End year
      }
    } else if (contextRef.startsWith('asof_')) {
      const parts = contextRef.split('_');
      if (parts.length >= 2) {
        return parts[1].substring(0, 4);
      }
    }
    return new Date().getFullYear().toString();
  };

  // Helper to extract member type from context ref
  const extractMemberType = (contextRef: string): string => {
    if (contextRef.includes('ConsolidatedMember')) return 'ConsolidatedMember';
    if (contextRef.includes('SeparateMember')) return 'SeparateMember';
    return '';
  };

  // Helper to detect multi-dimensional hypercube contexts (matches 1.0 line 2692-2720)
  const isMultiDimensionalHypercube = (contextRef: string): boolean => {
    const parts = contextRef.split('_');
    
    // Find base member index (ConsolidatedMember or SeparateMember)
    let baseMemberIndex = -1;
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === 'ConsolidatedMember' || parts[i] === 'SeparateMember') {
        baseMemberIndex = i;
        break;
      }
    }
    
    // If base member found and there are additional parts after it, check if they're member dimensions
    if (baseMemberIndex !== -1 && baseMemberIndex < parts.length - 1) {
      // Check if the additional parts look like member dimensions (end with "Member")
      for (let i = baseMemberIndex + 1; i < parts.length; i++) {
        if (parts[i].endsWith('Member')) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Helper to find sibling contexts (current and comparative)
  const findSiblingContexts = (contextRef: string, qname: string, fieldName: string) => {
    const memberType = extractMemberType(contextRef);
    
    // Check if this is a hypercube context (matches 1.0 line 2568-2628)
    const isHypercube = isMultiDimensionalHypercube(contextRef);
    
    if (isHypercube) {
      // For hypercube contexts, use single context mode (no comparative)
      devLog('Hypercube context detected - using single context mode');
      const year = extractYearFromContext(contextRef);
      return {
        current: contextRef,
        comparative: contextRef, // Same context for both
        currentYear: year,
        comparativeYear: year
      };
    }
    
    // Regular contexts: Find sibling contexts
    let allInputs: HTMLInputElement[] = [];

    // Try to find by qname first (for regular fields)
    if (qname) {
      allInputs = Array.from(document.querySelectorAll(`input[data-qname="${qname}"]`) as NodeListOf<HTMLInputElement>);
    }

    // If no inputs found (readonly fields without qname), search by field name
    if (allInputs.length === 0) {
      allInputs = Array.from(document.querySelectorAll(`input[name="${fieldName}"]`) as NodeListOf<HTMLInputElement>);
    }

    // Filter by member type (consolidated vs separate)
    const sameMemberInputs = allInputs.filter(input => {
      const inputContext = input.getAttribute('data-context-ref') || '';
      return extractMemberType(inputContext) === memberType;
    });

    // Find current and comparative contexts
    const contexts = sameMemberInputs.map(input => ({
      contextRef: input.getAttribute('data-context-ref') || '',
      year: extractYearFromContext(input.getAttribute('data-context-ref') || '')
    }));

    // Deduplicate contexts (same context may appear multiple times)
    const uniqueContexts = Array.from(
      new Map(contexts.map(c => [c.contextRef, c])).values()
    );

    // Sort by year descending (most recent first)
    uniqueContexts.sort((a, b) => parseInt(b.year) - parseInt(a.year));

    return {
      current: uniqueContexts[0]?.contextRef || contextRef,
      comparative: uniqueContexts[1]?.contextRef || contextRef,
      currentYear: uniqueContexts[0]?.year || new Date().getFullYear().toString(),
      comparativeYear: uniqueContexts[1]?.year || new Date().getFullYear().toString()
    };
  };

  // Handle footnote button click
  const handleFootnoteClick = useCallback((
    fieldLabel: string,
    fieldName: string,
    contextRef: string,
    qname: string,
    fieldId: string,
    isReadonly: boolean
  ) => {
    devLog('Opening footnote modal for:', { fieldLabel, fieldName, contextRef, qname });
    
    // Check if hypercube context
    const isHypercube = isMultiDimensionalHypercube(contextRef);
    devLog('Is hypercube context:', isHypercube);

    // Find sibling contexts (pass fieldName for readonly fields without qname)
    const contextPair = findSiblingContexts(contextRef, qname, fieldName);
    devLog('Context pair:', contextPair);
    
    const memberType = extractMemberType(contextRef);

    // Store in ref (no re-render)
    currentFootnoteFieldRef.current = {
      fieldId,
      fieldLabel,
      fieldName,
      contextRef,
      qname,
      currentYearContext: contextPair.current,
      comparativeYearContext: contextPair.comparative,
      currentYear: contextPair.currentYear,
      comparativeYear: contextPair.comparativeYear,
      memberType,
      isReadonly
    };

    // Open modal (NO STATE, NO RE-RENDER)
    if (currentFootnoteFieldRef.current) {
      const companyInfoForModal = companyInfoLoadedRef.current
        ? {
            consolidated: companyInfoLoadedRef.current.consolidated || 'N',
            first_year: companyInfoLoadedRef.current.first_year || 'N'
          }
        : { consolidated: 'N', first_year: 'N' };

      setFootnoteModalContext({
        fieldInfo: currentFootnoteFieldRef.current,
        companyInfo: companyInfoForModal,
        onUpdateField: handleFieldChange,
        onMarkDirty: () => {
          hasUnsavedChangesRef.current = true;
        }
      });
      openFootnoteModal();
    }
  }, [handleFieldChange]);

  // Initialize DOM-only footnote anchors/buttons once inputs exist
  useEffect(() => {
    const opts = {
      onMarkDirty: () => {
        hasUnsavedChangesRef.current = true;
      },
      onOpen: ({
        fieldId,
        fieldLabel: openLabel,
        fieldName: openFieldName,
        contextRef: openContextRef,
        qname: openQname,
        isReadonly
      }: {
        fieldId: string;
        fieldLabel: string;
        fieldName: string;
        contextRef: string;
        qname: string;
        isReadonly: boolean;
      }) => {
        handleFootnoteClick(
          openLabel,
          openFieldName,
          openContextRef,
          openQname,
          fieldId,
          isReadonly
        );
      }
    };

    initFootnoteAnchors(opts);
    const handleRefreshAnchors = () => refreshFootnoteAnchors(opts);
    // Only refresh anchors on initial form data load (not on every footnote save)
    // footnoteUpdated was removed - handleSave already updates specific buttons/anchors directly
    document.addEventListener('formDataLoaded', handleRefreshAnchors);
    
    // Sync ref/HyperFormula when footnote modal publishes value updates (editable fields only)
    const handleFootnoteValueUpdate = (e: Event) => {
      const { detail } = e as CustomEvent;
      if (!detail) return;
      const { fieldName, qname, contextRef, value } = detail;
      if (!contextRef || value === undefined) return;
      // Respect readonly fields: if all matching inputs are readonly, skip update
      const inputs = Array.from(
        document.querySelectorAll(
          `input[data-context-ref="${contextRef}"][data-qname="${qname}"], input[data-context-ref="${contextRef}"][name="${fieldName}"]`
        ) as NodeListOf<HTMLInputElement>
      );
      const allReadonly = inputs.length > 0 && inputs.every(inp =>
        inp.readOnly ||
        inp.hasAttribute('readonly') ||
        inp.getAttribute('aria-readonly') === 'true'
      );
      if (allReadonly) return;
      handleFieldChange(fieldName || qname, contextRef, value, qname);
    };
    document.addEventListener('footnoteValueUpdated', handleFootnoteValueUpdate);
    return () => {
      document.removeEventListener('formDataLoaded', handleRefreshAnchors);
      document.removeEventListener('footnoteValueUpdated', handleFootnoteValueUpdate);
    };
  }, [handleFootnoteClick, handleFieldChange]);

  // Helper function to collect footnote data from DOM for saving
  const collectFootnoteData = useCallback(() => {
    const footnoteData: Record<string, { footnote: string; breakdown: any[] }> = {};
    
    const safeParseBreakdown = (raw: string | null): any[] => {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('⚠️ Invalid footnote breakdown JSON on button attribute, ignoring.', { raw });
        return [];
      }
    };
    
    // Find all footnote buttons that have data
    const allFootnoteButtons = document.querySelectorAll('.footnote-edit-btn') as NodeListOf<HTMLButtonElement>;
    
    allFootnoteButtons.forEach(button => {
      const contextRef = button.getAttribute('data-context-ref');
      const qname = button.getAttribute('data-qname');
      const footnoteText = button.getAttribute('data-footnote-text');
      const breakdownStr = button.getAttribute('data-footnote-breakdown');
      
      if (contextRef && qname && (footnoteText || breakdownStr)) {
        const key = `${contextRef}_${qname}`;
        footnoteData[key] = {
          footnote: footnoteText || '',
          breakdown: safeParseBreakdown(breakdownStr)
        };
      }
    });
    
    return footnoteData;
  }, []);

  // ✅ DOM FREEZE BARRIER: Recreate 1.0's synchronous DOM guarantee
  // React reconciliation, HyperFormula recalc, modal portals can destabilize DOM
  // This ensures DOM is fully settled before we read it
  const waitForDomToSettle = async () => {
    // 1️⃣ Flush React + browser layout
    await new Promise(requestAnimationFrame);
    // 2️⃣ Flush microtasks (HF, modal close, refs)
    await Promise.resolve();
    // 3️⃣ Flush chained DOM mutations
    await new Promise(requestAnimationFrame);
  };

  // ✅ SESSION GUARD: Returns true if we hold a usable (non-expired) JWT.
  // IMPORTANT: do NOT call refreshSession() directly here — concurrent refreshes
  // (this + supabase auto-refresh + other tabs) can revoke the whole session via
  // refresh-token reuse detection. Use the shared serialized helper instead.
  const ensureValidSession = async (): Promise<boolean> => {
    // TEST HOOK (one-shot): run `window.__forceSessionExpired = true` in the
    // DevTools console, then click Save — simulates an expired session so the
    // re-login modal can be tested. Cleared after one use so the retried save
    // (after re-login) proceeds normally.
    if ((window as any).__forceSessionExpired) {
      delete (window as any).__forceSessionExpired;
      devLog('🧪 __forceSessionExpired set — simulating expired session');
      return false;
    }
    // Suppress AuthContext reactions while probing/recovering the session so a
    // dead refresh token cannot remount/navigate the form mid-save.
    setReloginGuard(true);
    try {
      const ok = await ensureFreshSession(60_000);
      if (ok) {
        setReloginGuard(false);
        return true;
      }
      return false; // guard stays active — caller opens the re-login modal
    } catch {
      return false; // guard stays active — caller opens the re-login modal
    }
  };

  // Open the re-login modal (keeps the form DOM intact); retries save on success.
  // The guard stays active through the whole modal flow (wrong-password retries
  // included) and is released either by the modal's Cancel button or by the
  // retried handleSave once a valid session is confirmed.
  const promptReloginThenRetrySave = () => {
    setReloginGuard(true);
    setReloginModalContext({
      email: user?.email ?? '',
      onSuccess: () => { handleSave(); },
    });
    openReloginModal();
  };

  const handleSave = async () => {
    if (!filingId) {
      alert('No filing ID available');
      return;
    }

    // Recover expired sessions BEFORE collecting/saving, so no work is lost
    const sessionOk = await ensureValidSession();
    if (!sessionOk) {
      promptReloginThenRetrySave();
      return;
    }

    try {
      startSaving();
      
      // ✅ CRITICAL: Wait for DOM to settle before reading
      // This recreates 1.0's synchronous DOM guarantee
      // Prevents "saved but reverted" bug caused by React reconciliation mid-save
      await waitForDomToSettle();
      
      devLog('💾 Saving changes...');
      
      // ✅ FIX #3: Snapshot footnote data ONCE, then never touch DOM again
      // Prevents crashes when footnote modals are closing during save
      const footnoteData = collectFootnoteData();
      
      // Build database format: { "contextRef": { "qname": { "value": "...", "metadata": {...} } } }
      const dataToSave: Record<string, Record<string, any>> = {};

      // Note sequence only reorders 720000/730000 text blocks in the DOM for editing.
      // Never rewrite global metadata.sequence by qname (that made 2024/2025 share one
      // sequence and broke footnote XML ids). Keep legacy unique DOM index; if the
      // toggle is ON, put taxonomy order back before collect, then re-apply after.
      const noteSeqWasOn = sortByNoteNoRef.current;
      try {
        if (noteSeqWasOn) {
          applyNoteSequenceDomOrder(false);
        }

        // ✅ STRONGER SELECTOR: Use [data-context-ref][data-qname] instead of .form-control
        // This matches exactly what we need to save, regardless of CSS class
        const inputs = document.querySelectorAll('[data-context-ref][data-qname]');
        devLog(`💾 Processing ${inputs.length} form inputs...`);
        
        // ✅ FIX #1: Pre-snapshot which textareas are in text-block-wrappers
        // Prevents dangerous ancestor traversal during React reconciliation
        const textareaEligibility = new WeakMap<Element, boolean>();
        inputs.forEach((element) => {
          if (element.tagName === 'TEXTAREA') {
            const isInWrapper = element.closest('.text-block-wrapper') !== null;
            const isHidden = element.classList.contains('hidden');
            // Only eligible if it's a hidden textarea inside a wrapper (the real data storage)
            textareaEligibility.set(element, isHidden && isInWrapper);
          }
        });
        
        inputs.forEach((element, index) => {
          const input = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          
          const contextRef = input.getAttribute('data-context-ref');
          const qname = input.getAttribute('data-qname');
          const noFill = input.getAttribute('data-no-fill');
          const balance = input.getAttribute('data-balance');
          const type = input.getAttribute('data-type');
          
          // Skip fields without valid qname or contextRef
          if (!qname || qname === 'null' || !contextRef) {
            return;
          }
          
          // ✅ FIX #1: Use pre-computed eligibility (NO ancestor traversal during save!)
          if (input.tagName === 'TEXTAREA' && !textareaEligibility.get(element)) {
            return;
          }
          
          // Initialize context object if needed
          if (!dataToSave[contextRef]) {
            dataToSave[contextRef] = {};
          }
          
          // Get value from DOM
          let cleanValue = input.value;
          
          // Clean numeric values (remove formatting)
          if (input.classList.contains('financial-input') && cleanValue) {
            try {
              const numericValue = parseFloat(cleanValue.replace(/,/g, ''));
              cleanValue = numericValue.toFixed(2);
            } catch {
              // Keep original value if parsing fails
            }
          }
          
          // Build field data — unique sequence per DOM input (2024 then 2025 → different n)
          const fieldData: any = {
            value: cleanValue,
            metadata: {
              no_fill: noFill || 'N',
              balance_type: balance,
              type: type,
              sequence: index
            }
          };
          
          // Add footnote metadata if exists
          const footnoteKey = `${contextRef}_${qname}`;
          if (footnoteData[footnoteKey]) {
            if (footnoteData[footnoteKey].footnote) {
              fieldData.metadata.footnote = footnoteData[footnoteKey].footnote;
            }
            if (footnoteData[footnoteKey].breakdown && footnoteData[footnoteKey].breakdown.length > 0) {
              fieldData.metadata.breakdown = footnoteData[footnoteKey].breakdown;
            }
          }
          
          dataToSave[contextRef][qname] = fieldData;
        });
      } finally {
        if (noteSeqWasOn) {
          applyNoteSequenceDomOrder(true);
        }
      }

      const inputs = document.querySelectorAll('[data-context-ref][data-qname]');
      
      // 🛡️ EMPTY-SAVE GUARD: dataToSave is built ENTIRELY from the live DOM.
      // If the form subtree isn't rendered at this moment (failed/partial load,
      // stale re-login retry closure, discarded tab restore), the collection
      // yields {} — and an empty DOM also has zero .has-error marks, so the
      // save would write form_data = {} with status 'validated', silently
      // WIPING the customer's filing. Refuse to write instead.
      if (inputs.length === 0 || Object.keys(dataToSave).length === 0) {
        console.error(
          `🛡️ Save blocked: collected ${inputs.length} inputs / ` +
          `${Object.keys(dataToSave).length} contexts — form is not rendered. ` +
          'Refusing to overwrite saved data with an empty form.'
        );
        endSaving();
        alert(
          'Save blocked: the form content is not fully loaded, so saving now would wipe your filing data.\n\n' +
          'Please refresh the page and try again. Your saved data has NOT been changed.'
        );
        return;
      }

      // 🛡️ SHRINK GUARD: form_data is replaced WHOLESALE, so a partially
      // rendered form (missing sections) would silently drop the rest.
      // Compare against the last known copy; a drastic drop needs explicit
      // user confirmation. (Normal edits never reduce the context count —
      // inputs are hidden via CSS, never unmounted.)
      const previousContextCount = Object.keys(supabaseDataRef.current?.form_data || {}).length;
      const collectedContextCount = Object.keys(dataToSave).length;
      if (previousContextCount > 0 && collectedContextCount < previousContextCount * 0.5) {
        console.warn(
          `🛡️ Suspicious shrink: ${previousContextCount} contexts previously saved, ` +
          `only ${collectedContextCount} collected from DOM.`
        );
        const proceed = window.confirm(
          'Warning: this save contains far less data than what is currently stored ' +
          `(${collectedContextCount} of ${previousContextCount} sections collected).\n\n` +
          'The form may not be fully loaded. Saving now could lose data.\n\n' +
          'Press Cancel to abort (recommended), or OK to save anyway.'
        );
        if (!proceed) {
          endSaving();
          return;
        }
      }

      devLog('💾 Saving to Supabase...', Object.keys(dataToSave).length, 'contexts');
      
      // ✅ FIX: If validation is disabled (debug mode), temporarily enable it for save
      // This ensures correct status (draft/validated) is determined
      const wasValidationDisabled = isValidationDisabled();
      if (wasValidationDisabled) {
        devLog('💾 Validation was disabled - temporarily enabling for save...');
        toggleValidation(); // Enable validation
        
        // Update the toggle button UI
        if (validationToggleBtnRef.current) {
          validationToggleBtnRef.current.textContent = '🟢 Validation ON';
          validationToggleBtnRef.current.title = 'Validation is ENABLED (click to disable)';
        }
      }
      
      // ✅ FIX: Trigger fresh validation and wait for state + DOM to update
      // This prevents race condition where user clicks Save before validation completes
      // Also: capture the fresh error count via the validationUpdated event, since
      // `errors` destructured from the hook at render time is stale after the hook's
      // internal ref reassignment (and we intentionally don't re-render).
      let freshErrorCount = 0;
      const captureErrorCount = (e: Event) => {
        const count = (e as CustomEvent).detail?.errorCount;
        if (typeof count === 'number') freshErrorCount = count;
      };
      document.addEventListener('validationUpdated', captureErrorCount);
      checkValidation(); // Trigger validation (updates errorsRef + DOM)
      await waitForDomToSettle(); // Wait for DOM updates (.has-error classes applied)
      document.removeEventListener('validationUpdated', captureErrorCount);
      
      // Update filings table (form_data + status in same table now)
      // NOTE: Use DOM to check if ANY errors exist (0 vs non-zero for status)
      // DOM count may be higher than actual errors (1 error can mark 2 elements)
      // But for status, we only care: 0 errors = validated, any errors = draft
      const hasAnyErrors = document.querySelectorAll('.has-error').length > 0;
      const newStatus = hasAnyErrors ? 'draft' : 'validated';
      devLog(`💾 Status: ${newStatus} (DOM has errors: ${hasAnyErrors}, validation count: ${freshErrorCount} errors)`);
      
      // Use .select() to verify the update actually worked
      // Supabase returns success even if 0 rows were updated (silent failure)
      const { data: updatedRows, error: filingError } = await supabase
        .from('filings')
        .update({
          form_data: dataToSave,
          status: newStatus,
          updated_by: user?.id || null
        })
        .eq('id', filingId)
        .select('id');
      
      if (filingError) {
        throw new Error(`Failed to save filing: ${filingError.message}`);
      }
      
      // Verify we actually updated a row
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error('Save failed: Filing record not found. Please refresh and try again.');
      }
      
      devLog('✅ Save complete!');
      
      // Note: If validation was disabled before save, it's now enabled
      // User can toggle it off again if they want to continue debugging
      if (wasValidationDisabled) {
        devLog('💡 Note: Validation was re-enabled for save and remains ON');
      }
      
      // Update cached data ref with saved data (prevents stale data on any re-load)
      supabaseDataRef.current = { form_data: dataToSave };
      
      // Clear unsaved changes flag
      hasUnsavedChangesRef.current = false;
      
      endSaving();
      
      // Prepare modal context (error count + exit callback) and open it.
      // The modal is always mounted and reads context from the bus on open,
      // so this does NOT trigger a parent re-render of the 12k+ input tree.
      setSaveSuccessModalContext({
        errorCount: freshErrorCount,
        onExit: () => {
          hasUnsavedChangesRef.current = false;
          navigate(`/all-filings/${selectedCompany?.company_id}`);
        }
      });
      // Defer until after call stack clears to avoid React reconciliation
      // collision while DOM is still being touched (was #1 cause of
      // "removeChild" crashes during save).
      queueMicrotask(() => {
        openSaveSuccessModal();
      });
      
    } catch (error: any) {
      console.error('❌ Save error:', error);
      endSaving();
      // JWT/auth failure mid-save (e.g. token revoked between the pre-check and
      // the write) → offer re-login instead of a dead-end alert. The form DOM is
      // untouched, so retrying after login saves the same data.
      const msg = String(error?.message || '');
      if (/jwt|expired|invalid token|not authenticated|401|PGRST301/i.test(msg)) {
        promptReloginThenRetrySave();
        return;
      }
      alert(`Failed to save: ${msg}`);
    }
  };


  const applyPdfOpenToDOM = useCallback((open: boolean) => {
    // Keep ref updated (for other code paths) but DO NOT use it for rendering
    isPdfViewerOpenRef.current = open;

    if (mainContentWrapRef.current) {
      mainContentWrapRef.current.classList.toggle('with-pdf-viewer', open);
    }
    if (subContentRef.current) {
      subContentRef.current.classList.toggle('with-pdf-split', open);
    }
    if (leftTabsRef.current) {
      leftTabsRef.current.style.display = open ? 'none' : '';
    }
    if (stickyDropdownRef.current) {
      stickyDropdownRef.current.style.display = open ? 'block' : 'none';
    }
    if (pdfColumnRef.current) {
      pdfColumnRef.current.classList.remove('pdf-viewer-column', 'pdf-viewer-hidden-container');
      pdfColumnRef.current.classList.add(open ? 'pdf-viewer-column' : 'pdf-viewer-hidden-container');
    }
  }, []);

  // Listen for PDF viewer toggles (NO STATE, instant DOM updates)
  useEffect(() => {
    // Ensure closed initially
    setPdfViewerOpen(false);

    const handler = (e: any) => {
      const open = !!e?.detail?.isOpen;
      applyPdfOpenToDOM(open);

      // Never overlap modals with PDF mode
      closeFootnoteModal();
      closeTextBlockModal();

      // Auto-collapse main sidebar while PDF is open (more horizontal space, no re-render)
      if (open) {
        const wasCollapsed = getSidebarCollapsed();
        if (!wasCollapsed) {
          forcedSidebarCollapseRef.current = true;
          setSidebarCollapsed(true);
        }
      } else if (forcedSidebarCollapseRef.current) {
        // Restore only if we forced it
        forcedSidebarCollapseRef.current = false;
        setSidebarCollapsed(false);
      }
    };

    document.addEventListener(PDF_VIEWER_CHANGED_EVENT, handler);
    // Apply initial state
    applyPdfOpenToDOM(false);

    return () => document.removeEventListener(PDF_VIEWER_CHANGED_EVENT, handler);
  }, [applyPdfOpenToDOM]);

  // Save button - DOM-controlled, listens to saveStateBus.
  // No React state involved, so zero re-renders across the 12k+ input tree.
  useEffect(() => {
    const handler = (e: Event) => {
      const saving = !!(e as CustomEvent).detail?.saving;
      const btn = saveButtonRef.current;
      const idle = saveBtnIdleRef.current;
      const busy = saveBtnBusyRef.current;
      if (!btn || !idle || !busy) return;
      btn.disabled = saving;
      idle.style.display = saving ? 'none' : 'inline-flex';
      busy.style.display = saving ? 'inline-flex' : 'none';
    };
    document.addEventListener(SAVE_STATE_CHANGED_EVENT, handler);
    return () => document.removeEventListener(SAVE_STATE_CHANGED_EVENT, handler);
  }, []);

  const handleComparePDF = useCallback(() => {
    togglePdfViewerOpen();
  }, []);

  // 🔧 DEBUG: Validation toggle (ref-based, no re-renders)
  const validationDisabledRef = useRef<boolean>(isValidationDisabled());
  const validationToggleBtnRef = useRef<HTMLButtonElement>(null);
  
  const handleToggleValidation = useCallback(() => {
    const isNowDisabled = toggleValidation();
    validationDisabledRef.current = isNowDisabled;
    
    // Update button text directly (no state, no re-render)
    if (validationToggleBtnRef.current) {
      validationToggleBtnRef.current.textContent = isNowDisabled ? '🔴 Validation OFF' : '🟢 Validation ON';
      validationToggleBtnRef.current.title = isNowDisabled 
        ? 'Validation is DISABLED (click to enable)' 
        : 'Validation is ENABLED (click to disable)';
    }
  }, []);

  const handleEditorToggle = useCallback((editorId: string | null) => {
    // If same field is already open, close it (toggle behavior)
    // Check by looking at the modal's current context, not the stale ref
    if (editorId && activeEditorRef.current === editorId) {
      // Same field clicked while modal is open - close it
      activeEditorRef.current = null;
      closeTextBlockModal();
      return;
    }
    
    // Store in ref
    activeEditorRef.current = editorId;
    
    if (editorId) {
      // Parse fieldId: "fieldName_contextRef"
      const parts = editorId.split('_');
      const fieldName = parts[0];
      const contextRef = parts.slice(1).join('_');
      
      // Find the textarea to get field label
      const textarea = document.querySelector(
        `textarea[name="${fieldName}"][data-context-ref="${contextRef}"]`
      ) as HTMLTextAreaElement;
      
      if (textarea) {
        const qname = textarea.getAttribute('data-qname') || '';
        const fieldLabel = qname.split(':')[1] || fieldName;
        
        // Format camelCase to readable text
        const formatLabel = (text: string) => {
          return text
            .replace(/([A-Z])/g, ' $1')
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .trim()
            .replace(/^\w/, c => c.toUpperCase());
        };
        
        currentTextBlockRef.current = {
          fieldName,
          fieldLabel: formatLabel(fieldLabel),
          contextRef
        };

        // Open TextBlockModal (NO STATE, NO RE-RENDER)
        setTextBlockModalContext({
          fieldName,
          fieldLabel: formatLabel(fieldLabel),
          contextRef,
          initialValue: textarea.value || '',
          onSave: (newValue) => {
            // Mark as dirty
            hasUnsavedChangesRef.current = true;
            // Update preview immediately (NO RE-RENDER) — prefer note title, not body/table text
            const preview = getTextBlockPreview(newValue);
            const textareas = document.querySelectorAll(
              `textarea[name="${fieldName}"][data-context-ref="${contextRef}"]`
            ) as NodeListOf<HTMLTextAreaElement>;

            textareas.forEach((ta) => {
              const wrapper = ta.closest('.text-block-wrapper');
              const previewEl = wrapper?.querySelector('.text-preview') as HTMLElement | null;
              if (!previewEl) return;

              // ✅ SIMPLE: Just use textContent (safest for React compatibility)
              previewEl.textContent = preview;
            });

            // Find textarea
            const target = document.querySelector(
              `textarea[name="${fieldName}"][data-context-ref="${contextRef}"]`
            ) as HTMLTextAreaElement | null;

            if (target) {
              const qname = target.getAttribute('data-qname');
              target.value = newValue;

              // Update formDataRef (for save)
              if (qname && contextRef) {
                const refKey = `${qname}_${contextRef}`;
                if (newValue && newValue.trim()) {
                  formDataRef.current[refKey] = newValue;
                } else {
                  delete formDataRef.current[refKey];
                }
              }

              // Trigger validation
              target.dispatchEvent(new Event('change', { bubbles: true }));
            }

            updateNoteOtherEmptyWarning();
          },
          onAfterClose: () => {
            activeEditorRef.current = null;
          }
        });
        openTextBlockModal();
      }
    } else {
      closeTextBlockModal();
    }
  }, []);

  // Get company info from ref (no memoization needed - ref doesn't trigger re-renders)
  const companyInfo = companyInfoLoadedRef.current ? {
    consolidated: companyInfoLoadedRef.current.consolidated || 'N',
    first_year: companyInfoLoadedRef.current.first_year || 'N',
    reporting_period: companyInfoLoadedRef.current.currentPeriod || companyInfoLoadedRef.current.reporting_period,
    previous_period: companyInfoLoadedRef.current.previousPeriod || companyInfoLoadedRef.current.previous_period
  } : {
    consolidated: 'N',
    first_year: 'N',
    reporting_period: { opening: '', start: '', end: '' },
    previous_period: { opening: '', start: '', end: '' }
  };

  // Context ref for simple (non-dimensional) fields — matches FormField.getPeriodContext without member suffix
  const getSimpleFieldContextRef = (field: any): string => {
    const period = companyInfo.reporting_period;
    if (!period || !period.start || !period.end) return 'context_default';

    const formatDate = (dateStr: string): string => {
      if (/^\d{8}$/.test(dateStr)) return dateStr;
      return dateStr.replace(/-/g, '');
    };

    const formattedStart = formatDate(period.start);
    const formattedEnd = formatDate(period.end);

    if (field.properties?.period_type === 'duration') {
      return `fromto_${formattedStart}_${formattedEnd}`;
    }
    return `asof_${formattedEnd}`;
  };

  /** Update Note sequence button chip without React re-render. */
  const updateNoteSeqButtonDom = (on: boolean) => {
    const btn = noteSeqBtnRef.current;
    const chip = noteSeqStateRef.current;
    if (btn) btn.classList.toggle('is-active', on);
    if (chip) {
      chip.textContent = on ? 'ON' : 'OFF';
      chip.classList.toggle('is-on', on);
      chip.classList.toggle('is-off', !on);
    }
  };

  const getTextBlockPlacementKey = (wrapper: HTMLElement): string => {
    const ta = wrapper.querySelector('textarea[data-qname][data-context-ref]') as HTMLTextAreaElement | null;
    if (!ta) return wrapper.getAttribute('data-field-id') || Math.random().toString(36);
    return `${ta.getAttribute('data-qname')}_${ta.getAttribute('data-context-ref')}`;
  };

  /**
   * Physically reorder text-block rows inside Note Policy / Note Other sections.
   * No React setState — moving nodes keeps textarea values intact.
   *
   * ON: leave a comment marker at each original spot, then flatten into section.
   * OFF: insertBefore each marker so titles like Significant Accounting Policies
   * return before .nested-children (appendChild alone cannot do that).
   */
  const applyNoteSequenceDomOrder = (enabled: boolean) => {
    const sectionRoots = document.querySelectorAll<HTMLElement>(
      '.sub-content-item[data-tab^="720000_"] .form-section, .sub-content-item[data-tab^="730000_"] .form-section'
    );

    sectionRoots.forEach((sectionEl) => {
      const wrappers = Array.from(
        sectionEl.querySelectorAll<HTMLElement>('.text-block-wrapper')
      );
      if (wrappers.length === 0) return;

      // Remember original taxonomy placement once (before first flat reorder)
      wrappers.forEach((el, i) => {
        if (!el.hasAttribute('data-taxonomy-index')) {
          el.setAttribute('data-taxonomy-index', String(i));
        }
        const key = getTextBlockPlacementKey(el);
        if (!noteSeqOrigPlacementRef.current.has(key) && el.parentElement) {
          const marker = document.createComment(`note-seq:${key}`);
          el.parentElement.insertBefore(marker, el);
          noteSeqOrigPlacementRef.current.set(key, { marker });
        }
      });

      if (!enabled) {
        const sortedBack = [...wrappers].sort((a, b) => {
          const ai = parseInt(a.getAttribute('data-taxonomy-index') || '0', 10);
          const bi = parseInt(b.getAttribute('data-taxonomy-index') || '0', 10);
          return ai - bi;
        });
        sortedBack.forEach((el) => {
          const key = getTextBlockPlacementKey(el);
          const marker = noteSeqOrigPlacementRef.current.get(key)?.marker;
          if (marker?.parentNode) {
            marker.parentNode.insertBefore(el, marker);
          }
        });
        return;
      }

      const sorted = [...wrappers].sort((a, b) => {
        const aVal = (a.querySelector('textarea[data-type="text"]') as HTMLTextAreaElement | null)?.value || '';
        const bVal = (b.querySelector('textarea[data-type="text"]') as HTMLTextAreaElement | null)?.value || '';
        const aHas = !!aVal.trim();
        const bHas = !!bVal.trim();
        if (aHas !== bHas) return aHas ? -1 : 1;

        const cmp = compareReportNoteSortKeys(
          getReportNoteSortKeyFromHtml(aVal),
          getReportNoteSortKeyFromHtml(bVal)
        );
        if (cmp !== 0) return cmp;

        const ai = parseInt(a.getAttribute('data-taxonomy-index') || '0', 10);
        const bi = parseInt(b.getAttribute('data-taxonomy-index') || '0', 10);
        return ai - bi;
      });

      // Flatten into the section root in report-note order (global across abstracts)
      // Markers stay in place for an exact OFF restore.
      sorted.forEach((el) => sectionEl.appendChild(el));
    });
  };

  const handleToggleNoteSequence = () => {
    const next = !sortByNoteNoRef.current;
    sortByNoteNoRef.current = next;
    updateNoteSeqButtonDom(next);
    applyNoteSequenceDomOrder(next);
  };

  // Helper to render a single input field
  const renderInputField = (field: any, index: number, formData: Record<string, any>, onFieldChange: (name: string, contextRef: string, value: string) => void) => {
    // Get contextRef for this field (needed for error checking)
    const fieldContextRef = getSimpleFieldContextRef(field);
    // Note: Error indicators now handled by DOM manipulation in useValidation.ts (no props needed)
    
    // Text fields now use same left-right layout as other fields
    // FormField component handles the layout internally
    if (field.properties?.data_type === 'text') {
      return (
        <FormField
          key={`${field.name}_${index}`}
          field={field}
          companyInfo={companyInfo}
          formData={formData}
          onFieldChange={onFieldChange}
          activeEditor={activeEditorRef.current}
          onEditorToggle={handleEditorToggle}
        />
      );
    }

    // Other fields use horizontal layout (left-right)
    return (
      <div key={`${field.name}_${index}`} className="form-group row">
        <div className="col-2">
          <label className="form-label" htmlFor={field.name}>
            {field.standard_label}
            {/* Red asterisk for mandatory fields (matches 1.0 lines 635-637) */}
            {isMandatoryField(field.qname) && <span style={{ color: 'red' }}> *</span>}
            {/* Field code for exemption applications (matches 1.0 lines 638-640) */}
            {getFieldCode(field.qname) && <span style={{ color: 'red', fontSize: '1em' }}> ({getFieldCode(field.qname)})</span>}
          </label>
        </div>
        <div className="col-2">
          <FormField
            field={field}
            companyInfo={companyInfo}
            formData={formData}
            onFieldChange={onFieldChange}
            activeEditor={activeEditorRef.current}
            onEditorToggle={handleEditorToggle}
          />
          {field.documentation && (
            <div className="form-text text-muted">{field.documentation}</div>
          )}
        </div>
      </div>
    );
  };

  // Render field based on field structure - matches your 1.0 macro logic
  // formData parameter is the ref (single source of truth) - used for defaultValue
  const renderField = (field: any, index: number, formData: Record<string, any>, onFieldChange: (name: string, contextRef: string, value: string) => void) => {
    // If field has children, it's a container/section
    if (field.structure?.children && field.structure.children.length > 0) {
      // Handle auditor information sections (matches 1.0 lines 450-586)
      if (field.name === "AuditorsInformationTable" || field.name === "AuditorsInformationLineitem") {
        return (
          <AuditorsInformationTable
            key={`${field.name}_${index}`}
            field={field}
            companyInfo={companyInfo}
            formData={formData}
            onFieldChange={onFieldChange}
          />
        );
      }

      // Handle business table section (matches 1.0 lines 658-750)
      if (field.name === "DetailsOfPrincipalActivitiesOfBusinessAbstract") {
        return (
          <BusinessTable
            key={`${field.name}_${index}`}
            field={field}
            companyInfo={companyInfo}
            formData={formData}
            onFieldChange={onFieldChange}
            hasFieldError={hasFieldError}
          />
        );
      }
      
      // Handle CLBG category table (matches 1.0 lines 752-833)
      if (field.name === "DisclosureOfCompaniesLimitedByGuaranteeAbstract") {
        return (
          <CLBGTable
            key={`${field.name}_${index}`}
            field={field}
            companyInfo={companyInfo}
            formData={formData}
            onFieldChange={onFieldChange}
            hasFieldError={hasFieldError}
          />
        );
      }

      // Check if this is a table-type section (Abstract fields)
      const MBRStableFields = [
        "StatementOfFinancialPositionAbstract",
        "StatementOnSubclassificationOfAssetsLiabilitiesAndEquityAbstract",
        "StatementOfProfitOrLossAbstract",
        "AnalysisOfProfitOrLossAbstract",
        "ComprehensiveIncomeNetOfTaxAbstract",
        "ComprehensiveIncomeBeforeTaxAbstract",
        "StatementOfCashFlowsAbstract",
        "StatementOfIncomeAndExpenditureAbstract",
        "AnalysisOfIncomeAndExpenseAbstract",

      ];

      const SingaporeTableFields = [
        "StatementOfFinancialPositionLineItems",
        "StatementOfProfitOrLossLineItems",
        "StatementOfCashFlowsLineItems",
        "DisclosureOfDetailedInformationAboutTradeAndOtherReceivablesLineItems",
        "DisclosureOfDetailedInformationAboutTradeAndOtherPayablesLineItems",
        "DisclosureOfRevenueLineItems",
        "DisclosureOfSelectedIncomeExpensesLineItems",
        "DisclosureOfLoansAndBorrowingsLineItems",
        "DisclosureOfTransactionsBetweenRelatedPartiesLineItems",
        "DisclosureOfRightofuseAssetsItems",
      ];

      const isMalaysiaFiling = resolvedCountryIso === 'MY';
      const isSingaporeFiling = resolvedCountryIso === 'SG';
      const isHypercubeTableField =
        MBRStableFields.includes(field.name) || SingaporeTableFields.includes(field.name);

      if (isHypercubeTableField) {
        if (isSingaporeFiling) {
          return (
            <SOFPTableSG
              key={`${field.name}_${index}`}
              section={field}
              companyInfo={companyInfo}
              negativeTargets={negativeTargetsRef.current}
              globalHyperFormula={{
                isReady: hyperFormula.isReady,
                readOnlyFields: hyperFormula.readOnlyFields,
                handleFieldChange: hyperFormula.handleFieldChange,
                getCalculatedValue: hyperFormula.getCalculatedValue
              }}
              onFieldChange={onFieldChange}
              formData={formData}
              onFootnoteClick={handleFootnoteClick}
              duplicateFields={crossFieldNav.duplicateFields}
              onShowNavigationMenu={crossFieldNav.showNavigationMenu}
            />
          );
        }

        return (
          <SOFPTable
            key={`${field.name}_${index}`}
            section={field}
            companyInfo={companyInfo}
            negativeTargets={negativeTargetsRef.current}
            globalHyperFormula={{
              isReady: hyperFormula.isReady,
              readOnlyFields: hyperFormula.readOnlyFields,
              handleFieldChange: hyperFormula.handleFieldChange,
              getCalculatedValue: hyperFormula.getCalculatedValue
            }}
            onFieldChange={onFieldChange}
            formData={formData}
            onFootnoteClick={handleFootnoteClick}
            duplicateFields={crossFieldNav.duplicateFields}
            onShowNavigationMenu={crossFieldNav.showNavigationMenu}
          />
        );
      }

      // Equity/Fund statements (Malaysia SOCIE)
      // Note: Singapore PPE (DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract)
      // is handled at section level, not here
      if (isMalaysiaFiling && (field.name === "StatementOfChangesInEquityAbstract" || field.name === "StatementOfChangesInFundAbstract")) {
        return (
          <SOCIETable
            key={`${field.name}_${index}`}
            section={field}
            companyInfo={companyInfo}
            negativeTargets={negativeTargetsRef.current}
            onFieldChange={onFieldChange}
            formData={formData}
          />
        );
      }

      // Statement of Retained Earnings
      if (field.name === "StatementOfRetainedEarningsAbstract") {
        return (
          <SORETable
            key={`${field.name}_${index}`}
            section={field}
            companyInfo={companyInfo}
            negativeTargets={negativeTargetsRef.current}
            globalHyperFormula={{
              isReady: hyperFormula.isReady,
              readOnlyFields: hyperFormula.readOnlyFields,
              handleFieldChange: hyperFormula.handleFieldChange,
              getCalculatedValue: hyperFormula.getCalculatedValue
            }}
            onFieldChange={onFieldChange}
            formData={formData}
            onFootnoteClick={handleFootnoteClick}
          />
        );
      }


      // Note tables with horizontal sums only (Share Capital, Related Party)
      // Force first_year='Y' for Note tables (they only show current year, no comparative)
      if (field.name === "DisclosureOfClassesOfShareCapitalAbstract" ||
        field.name === "DisclosureOfTransactionsBetweenRelatedPartiesAbstract") {
        return (
          <NoteShareRelatedPartyTable
            key={`${field.name}_${index}`}
            section={field}
            companyInfo={{
              ...companyInfo,
              first_year: 'Y' // Force single year mode for Note tables
            }}
            negativeTargets={negativeTargetsRef.current}
            formData={formData}
            onFootnoteClick={handleFootnoteClick}
          />
        );
      }

      // Singapore PPE Table (3D hypercube: PPE classes × line items × period/consolidated)
      // When we see the Table element, find the PARENT section to pass both Table and LineItems
      if (field.name === "DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentTable") {
        // Find the parent section that contains both Table and LineItems
        const parentSection = formStructureRef.current?.sections['32000000_DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentAbstract'];
        if (parentSection) {
          return (
            <PPETableSG
              key={`${field.name}_${index}`}
              section={parentSection}
              companyInfo={companyInfo}
              negativeTargets={negativeTargetsRef.current}
              onFieldChange={onFieldChange}
              formData={formData}
            />
          );
        }
      }

      // Skip LineItems for Singapore PPE - already handled by the Table check above
      if (field.name === "DisclosureOfDetailedInformationAboutPropertyPlantAndEquipmentLineItems") {
        return null;
      }

      // Singapore Intangible Assets Table (3D hypercube: same structure as PPE)
      // When we see the Table element, find the PARENT section to pass both Table and LineItems
      if (field.name === "DisclosureOfDetailedInformationAboutIntangibleAssetsTable") {
        // Find the parent section that contains both Table and LineItems
        const parentSection = formStructureRef.current?.sections['33000000_DisclosureOfIntangibleAssetsAbstract'];
        if (parentSection) {
          return (
            <PPETableSG
              key={`${field.name}_${index}`}
              section={parentSection}
              companyInfo={companyInfo}
              negativeTargets={negativeTargetsRef.current}
              onFieldChange={onFieldChange}
              formData={formData}
            />
          );
        }
      }

      // Skip LineItems for Singapore Intangible Assets - already handled by the Table check above
      if (field.name === "DisclosureOfDetailedInformationAboutIntangibleAssetsLineItems") {
        return null;
      }

      // Distinguish between top-level sections and nested containers
      const isTopLevelSection = field.name.includes("Abstract");

      if (isTopLevelSection) {
        // Show header for main sections like "Filing Information"
        return (
          <div key={`${field.name}_${index}`} className="form-section">
            <div className="section-header">
              <h3>{field.standard_label}</h3>
            </div>
            {field.name === 'GeneralFilingInformationAbstract' && (
              <>
                <div className="year-end-tip-banner" role="note">
                  <span className="year-end-tip-badge">Important</span>
                  <span className="year-end-tip-text">
                    Verify the year-end dates first — changing them later will clear your other edits.
                  </span>
                  <a
                    href="#"
                    className="year-end-tip-link"
                    onClick={(e) => {
                      e.preventDefault();
                      document.dispatchEvent(new CustomEvent('openYearEndModal'));
                    }}
                  >
                    Modify Year End
                  </a>
                </div>
                <div
                  id="note-other-empty-warning"
                  className="year-end-tip-banner note-other-warning-banner"
                  role="note"
                  style={{ display: 'none' }}
                >
                  <span className="year-end-tip-badge">Warning</span>
                  <span className="year-end-tip-text">
                    Note - Other is empty. Contact support immediately if you need to re-convert the filing again (this will clear your other edits)
                  </span>
                </div>
              </>
            )}
            <div className="section-content">
              {field.structure.children.map((childField: any, childIndex: number) =>
                renderField(childField, childIndex, formData, onFieldChange)
              )}
            </div>
          </div>
        );
      }

      // If field has children but is NOT abstract, it's a parent input field
      // Render the field itself AND its children
      if (!field.properties?.abstract) {
        return (
          <React.Fragment key={`${field.name}_${index}`}>
            {/* Render the field itself */}
            {renderInputField(field, index, formData, onFieldChange)}

            {/* Render children indented */}
            <div className="nested-children">
              {field.structure.children.map((childField: any, childIndex: number) =>
                renderField(childField, childIndex, formData, onFieldChange)
              )}
            </div>
          </React.Fragment>
        );
      }

      // Handle nested abstract fields with children - matches 1.0 logic
      // In 1.0: {% elif item.properties.abstract and item.structure.children %}
      // Just render children directly without any container or header
      return (
        <React.Fragment key={`${field.name}_${index}`}>
          {field.structure.children.map((childField: any, childIndex: number) =>
            renderField(childField, childIndex, formData, onFieldChange)
          )}
        </React.Fragment>
      );
    }

    // If field has no children, it's an input field
    if (!field.properties?.abstract) {
      return renderInputField(field, index, formData, onFieldChange);
    }


    return null;
  };

  // Get the active section data - Read from ref (not used anymore since sections render based on DOM)
  // const activeSectionData = formStructureRef.current?.sections[activeSectionRef.current as keyof typeof formStructureRef.current.sections];


  return (
    <MainLayout>
      {({ toggleSidebar }: { toggleSidebar?: () => void }) => (
        <form className="main-form" id="xbrlForm" style={{ position: 'relative' }}>
          {/* Loading overlay - only covers the form content area */}
          {(!metadataLoaded || !isFormReady) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '1rem',
              zIndex: 1
            }}>
              {loadingError ? (
                <>
                  <div style={{ fontSize: '48px', color: '#dc3545' }}>⚠️</div>
                  <p style={{ color: '#dc3545', fontSize: '18px', fontWeight: 'bold' }}>Failed to Load</p>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '400px', textAlign: 'center' }}>
                    {loadingError}
                  </p>
                  <button 
                    onClick={() => window.location.reload()}
                    style={{
                      marginTop: '1rem',
                      padding: '10px 20px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Retry
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ color: '#666', fontSize: '16px' }}>
                    {!metadataLoaded ? 'Loading taxonomy structure...' : 'Loading company data...'}
                  </p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </>
              )}
            </div>
          )}
          {/* Modern Panel Header */}
          <div className="main-panel">
            <div className="panel-item panel-item-left">
              {/* {toggleSidebar && <SidebarToggle onClick={toggleSidebar} />} */}
              {/* <Separator orientation="vertical" className="mr-2 h-4" /> */}
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => {
                      if (hasUnsavedChangesRef.current) {
                        const confirmLeave = window.confirm(
                          'You have unsaved changes. Are you sure you want to leave this page?\n\nAll unsaved changes will be lost.'
                        );
                        if (!confirmLeave) return;
                        hasUnsavedChangesRef.current = false;
                      }
                      navigate(`/all-filings/${selectedCompany?.company_id}`);
                    }}>
                      All Filings
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      {(() => {
                        const parts = breadcrumbTitle.split(' • ');
                        if (parts.length === 2) {
                          return (
                            <span className="flex items-center gap-2">
                              <span>{parts[0]}</span>
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 border border-blue-200">
                                {parts[1]}
                              </span>
                            </span>
                          );
                        }
                        return breadcrumbTitle;
                      })()}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div className="panel-item panel-item-right">
              <div className="panel-actions">
                {/* 🔧 DEBUG: Validation Toggle Button */}
                <button
                  ref={validationToggleBtnRef}
                  type="button"
                  className="action-btn action-btn-secondary"
                  onClick={handleToggleValidation}
                  title={validationDisabledRef.current ? 'Validation is DISABLED (click to enable)' : 'Validation is ENABLED (click to disable)'}
                  
                >
                  {validationDisabledRef.current ? '🔴 Validation OFF' : '🟢 Validation ON'}
                </button>
                <button
                  ref={noteSeqBtnRef}
                  type="button"
                  className="action-btn action-btn-secondary"
                  onClick={handleToggleNoteSequence}
                  title="ON: sort Accounting Policy and Note - Other text blocks by note sequence (1, 2, 3…) for easier editing. OFF: taxonomy order. Save always uses taxonomy identity (qname + context). DOM-only — no page re-render."
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-3-3m3 3l3-3" />
                  </svg>
                  <span>Note</span>
                  <span ref={noteSeqStateRef} className="note-seq-state is-off">
                    OFF
                  </span>
                </button>

                {/* Modern Action Buttons */}
                <button
                  type="button"
                  className="action-btn action-btn-secondary"
                  onClick={handleComparePDF}
                  title="Compare with PDF"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                  <span>Compare PDF</span>
                </button>

                <button
                  ref={saveButtonRef}
                  type="button"
                  className="action-btn action-btn-primary"
                  onClick={handleSave}
                  title="Save Changes"
                >
                  {/* Both states are always rendered; visibility + disabled
                      are toggled via DOM by the saveStateBus listener - no
                      React re-render involved. */}
                  <span
                    ref={saveBtnIdleRef}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17,21 17,13 7,13 7,21" />
                      <polyline points="7,3 7,8 15,8" />
                    </svg>
                    <span>Save</span>
                  </span>
                  <span
                    ref={saveBtnBusyRef}
                    style={{ display: 'none', alignItems: 'center', gap: '6px' }}
                  >
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </span>
                </button>

              </div>
            </div>
          </div>

          {/* Form Content - matches your 1.0 main-content structure */}
          <div className="main-content">
            <div ref={mainContentWrapRef} className="main-content-wrap">
              {/* Left - Tab navigation (DOM hidden when PDF open) */}
              <div ref={leftTabsRef} className="sub-panel">
                <FormSectionTabs
                  sections={sections}
                  onSectionChange={handleSectionChange}
                  getSectionErrorCount={getSectionErrorCount}
                />
              </div>

              {/* Middle - Tab contents */}
              <div ref={subContentRef} className="sub-content scrollable-element">
                {/* Sticky dropdown (always mounted; DOM shown when PDF open) */}
                <div ref={stickyDropdownRef} className="sticky-section-header" style={{ display: 'none' }}>
                  <FormSectionTabs
                    sections={sections}
                    onSectionChange={handleSectionChange}
                    getSectionErrorCount={getSectionErrorCount}
                    isCompactMode={true}
                  />
                </div>
                {/* Render ALL sections (like 1.0) - visibility controlled by handleSectionChange via DOM */}
                {formStructureRef.current && sections.map((section) => {
                  const sectionData = formStructureRef.current.sections[section.code as keyof typeof formStructureRef.current.sections];

                  return (
                    <div
                      key={section.code}
                      className="sub-content-item"
                      id={section.code}
                      style={{ display: 'none' }}
                      data-tab={section.code}
                      data-section-name={section.name}
                    >
                      <div className="form-section">
                        {/* Always taxonomy React order; Note sequence toggle reorders DOM only */}
                        {sectionData?.structure?.children &&
                          sectionData.structure.children.map((field: any, index: number) =>
                            renderField(field, index, formDataRef.current, handleFieldChange)
                          )
                        }
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right - PDF Viewer (always mounted, just hidden with CSS to preserve scroll) */}
              <div ref={pdfColumnRef} className="pdf-viewer-hidden-container">
                <PDFViewer
                  isOpen={true}
                  onClose={() => setPdfViewerOpen(false)}
                  orgId={filingOrgIdRef.current || undefined}
                  filingId={filingId}
                />
              </div>
            </div>
          </div>

          {/* Footnote Modal - always mounted (NO STATE, DOM controlled) */}
          <FootnoteModal />
          
          {/* Text Block Modal - always mounted (NO STATE, DOM controlled) */}
          <TextBlockModal />
          
          {/* Year End Change Modal - always mounted (NO STATE, DOM controlled) */}
          <YearEndChangeModal />
          
          {/* Expenses Method Change Modal - always mounted (NO STATE, DOM controlled) */}
          <ExpensesMethodChangeModal />
          
          {/* Equity Method Change Modal - always mounted (NO STATE, DOM controlled) */}
          <EquityMethodChangeModal />

          <SofpMethodChangeModal />

          <CashFlowsMethodChangeModal />
          
          {/* Cross-Field Navigation Menu - always mounted (NO STATE, DOM controlled, Event-driven like modals) */}
          <CrossFieldNavigationMenu />
          
          {/* Save Success Modal - always mounted (NO STATE, DOM controlled via saveSuccessModalBus) */}
          <SaveSuccessModal />


          <DirectorCrossSuggestBanner />
        </form>
      )}
    </MainLayout>
  );
}



